import { PencilIcon, TrashIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/ui/button';
import { useStore } from '~/store';
import type { Clip } from '~/types/pipeline';

type EditState = {
  id: string;
  value: string;
};

function clipFrameCount(clip: Clip): number {
  return clip.source.kind === 'cells' ? clip.source.cells.length : clip.source.blobs.length;
}

function clipKindLabel(clip: Clip): string {
  return clip.source.kind === 'cells' ? 'cells' : 'pins';
}

export function ClipsSection() {
  const clips = useStore((s) => s.clips.list);
  const activeId = useStore((s) => s.clips.activeId);
  const setActiveClip = useStore((s) => s.setActiveClip);
  const renameClip = useStore((s) => s.renameClip);
  const deleteClip = useStore((s) => s.deleteClip);
  const [editing, setEditing] = useState<EditState | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    if (clips.some((clip) => clip.id === editing.id)) return;
    setEditing(null);
  }, [clips, editing]);

  useEffect(() => {
    if (!editing) return;
    editInputRef.current?.focus();
    editInputRef.current?.select();
  }, [editing]);

  const commitRename = () => {
    if (!editing) return;
    renameClip(editing.id, editing.value);
    setEditing(null);
  };

  return (
    <section className="space-y-1.5 rounded border p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Clips
        </div>
      </div>
      {clips.length === 0 ? (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Select cells on the canvas, then use the popover to create a clip.
        </p>
      ) : (
        <ul className="space-y-1">
          {clips.map((clip) => {
            const isEditing = editing?.id === clip.id;
            const frameCount = clipFrameCount(clip);
            return (
              <li
                key={clip.id}
                className="flex items-center gap-2 rounded border bg-card px-2 py-1 text-xs"
              >
                <input
                  type="radio"
                  name="active-clip"
                  aria-label={clip.name}
                  checked={clip.id === activeId}
                  onChange={() => setActiveClip(clip.id)}
                />
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <input
                      ref={editInputRef}
                      value={editing.value}
                      onChange={(event) =>
                        setEditing((current) =>
                          current ? { ...current, value: event.target.value } : current,
                        )
                      }
                      onBlur={commitRename}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') commitRename();
                        if (event.key === 'Escape') setEditing(null);
                      }}
                      className="h-6 w-full rounded border bg-background px-1.5 text-xs outline-none focus-visible:border-ring"
                    />
                  ) : (
                    <span className="truncate">{clip.name}</span>
                  )}
                </div>
                <span
                  className="rounded border px-1 py-0.5 text-[10px] leading-none text-muted-foreground"
                  title={`${frameCount} ${clipKindLabel(clip)}`}
                >
                  {frameCount}f
                </span>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Rename ${clip.name}`}
                  onClick={() => setEditing({ id: clip.id, value: clip.name })}
                >
                  <PencilIcon />
                </Button>
                <Button
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Delete ${clip.name}`}
                  onClick={() => deleteClip(clip.id)}
                >
                  <TrashIcon />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
